import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageHeader from '../../components/owner/PageHeader';
import GymIdMemberSearch from '../../components/receptionist/GymIdMemberSearch';
import {
  ColumnFilterHeader,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import PrintActionsHeader from '../../components/shared/PrintActionsHeader';
import TablePagination from '../../components/shared/TablePagination';
import { formatCurrency } from './receptionistData';
import { getGymMemberId } from '../admin/members/memberData';
import {
  ensureWalkInMember,
  isWalkInGymMemberId,
  isWalkInMember,
  isWalkInMemberName,
  WALK_IN_GYM_MEMBER_ID,
  WALK_IN_PAYMENT_NAME,
} from '../../services/receptionist/walkInMember';
import { MEMBER_SEARCH_LABEL, MEMBER_SEARCH_PLACEHOLDER } from './sales/salePageConfig';
import SaleDurationField from './sales/SaleDurationField';
import SaleAmountFields from './sales/SaleAmountFields';
import SaleReceiptModal from './sales/SaleReceiptModal';
import PayBalanceModal from './sales/PayBalanceModal';
import RemainingClearHistory from './sales/RemainingClearHistory';
import SaleHistoryRowActions from './sales/SaleHistoryRowActions';
import SaleEditModal from './sales/SaleEditModal';
import { buildSaleReceiptData, buildReceiptFromPaymentRow } from './sales/buildSaleReceipt';
import {
  GUEST_PASS_DURATION_OPTIONS,
  GUEST_PASS_DURATION_VALUES,
  emptyGuestPassPeriodFields,
  formatDurationLabel,
  guestPassEndDate,
  syncGuestPassPeriodFields,
} from './sales/saleDurationUtils';
import { saleRowStartDate, saleRowEndDate, saleRowDuration, saleRowPackageName } from './sales/salePeriodUtils';
import {
  formatDateFilterValue,
  matchSaleHistoryFilters,
  saleHistoryFilterDefaults,
} from './sales/saleHistoryFilters';
import {
  SaleMethodColumnHeaders,
  SaleMethodCells,
  SALE_METHOD_COLUMN_COUNT,
} from './sales/SaleMethodTableColumns';
import {
  fetchMembersPage,
  fetchGuestPassPaymentHistory,
  createGuestPass,
  createPayment,
  deletePayment,
} from '../../services/receptionist/receptionistApi';
import { notifyPaymentsUpdated, PAYMENTS_UPDATE_EVENT } from './paymentStore';
import { usePanelToast } from '../../context/PanelToastContext';
import { isReceptionistPanelScope, RECEPTIONIST_BRANCH_EVENT } from '../../services/gym/branchContext';
import { pkTodayRange } from '../../utils/pkTime';
import { useSaleWriteBranch, SaleBranchField } from '../../hooks/useSaleWriteBranch';
import { dedupeMembers } from '../../utils/dedupeList';
import {
  combineMethodLabels,
  defaultPaymentSplits,
  serializePaymentSplits,
} from '../../utils/splitPaymentUtils';
import {
  resolveSalePayment,
  saleRowLeft,
  saleRowPaid,
  saleRowTotal,
} from './sales/salePaymentUtils';
import {
  exportGuestPassCsv,
  exportGuestPassExcel,
  exportGuestPassPdf,
  exportGuestPasses,
  printGuestPassList,
} from './exportGuestPasses';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../owner/finance/finance.css';
import '../admin/memberships/memberships.css';
import './members/members.css';
import './sales.css';
import './sales/ancillarySales.css';

const HISTORY_COL_COUNT = 9 + SALE_METHOD_COLUMN_COUNT + 3;

function todayDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function saleDate(row) {
  return String(row.date || '').slice(0, 10) || '—';
}

function emptyForm() {
  const today = todayDate();
  return {
    phone: '',
    ...emptyGuestPassPeriodFields(today),
    amount: '',
    paymentSplits: defaultPaymentSplits(),
    date: today,
    note: '',
  };
}

function Visitors() {
  const toast = usePanelToast();
  const splitsTouchedRef = useRef(false);
  const [sales, setSales] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState({ name: '', gymId: '', member: null });
  const {
    writeBranch,
    branches: writeBranches,
    pickedBranch,
    setPickedBranch,
    showPicker: showBranchPicker,
  } = useSaleWriteBranch(selected.member, selected.gymId);
  const [receipt, setReceipt] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastSavedId, setLastSavedId] = useState('');
  const receptionistScope = isReceptionistPanelScope();
  const todayRange = pkTodayRange();
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const listRangeStart = receptionistScope ? todayRange.startDate : rangeStart;
  const listRangeEnd = receptionistScope ? todayRange.endDate : rangeEnd;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [collected, setCollected] = useState(0);
  const pageSize = 50;
  const requestIdRef = useRef(0);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters(saleHistoryFilterDefaults());
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilters, listRangeStart, listRangeEnd]);

  const loadData = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    try {
      setError('');
      setLoading(true);
      const result = await fetchGuestPassPaymentHistory({
        page,
        pageSize,
        filters: {
          ...debouncedFilters,
          rangeStart: listRangeStart,
          rangeEnd: listRangeEnd,
        },
      });
      if (reqId !== requestIdRef.current) return;
      setSales(result.rows || []);
      setTotal(result.total || 0);
      setCollected(Number(result.collected) || 0);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setError(err.message || 'Failed to load guest passes');
      setSales([]);
      setTotal(0);
      setCollected(0);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedFilters, listRangeStart, listRangeEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refresh = () => loadData();
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => {
      window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
      window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    };
  }, [loadData]);

  // Search members as the receptionist types (name, serial, phone, CNIC)
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (!q) {
      setMembers(selected.member ? [selected.member] : []);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 25 });
        if (!cancelled) setMembers(dedupeMembers(result.rows || []));
      } catch {
        if (!cancelled) setMembers([]);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, selected.member]);

  const filteredSales = useMemo(() => (
    sales.filter((row) => matchSaleHistoryFilters(row, filters, {
      dateFn: saleDate,
      rangeStart: listRangeStart,
      rangeEnd: listRangeEnd,
    }))
  ), [sales, filters, listRangeStart, listRangeEnd]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const nextPage = () => setPage((p) => Math.min(totalPages, p + 1));
  const prevPage = () => setPage((p) => Math.max(1, p - 1));

  const totalCollected = useMemo(() => {
    const pagePaid = filteredSales.reduce((sum, row) => sum + saleRowPaid(row), 0);
    if (filteredSales.length !== sales.length) return pagePaid;
    return collected || pagePaid;
  }, [filteredSales, sales.length, collected]);

  const handleExport = async (exporterFn) => {
    try {
      const result = await fetchGuestPassPaymentHistory({
        filters: {
          ...debouncedFilters,
          rangeStart: listRangeStart,
          rangeEnd: listRangeEnd,
        },
        forExport: true,
      });
      const rows = (result.rows || []).filter((row) => matchSaleHistoryFilters(row, filters, {
        dateFn: saleDate,
        rangeStart: listRangeStart,
        rangeEnd: listRangeEnd,
      }));
      exporterFn(rows);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePeriodChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => syncGuestPassPeriodFields(prev, { [name]: value }));
  };

  const handleMemberSelect = ({ name, gymId, member }) => {
    setSelected({ name, gymId, member });
    if (member?.phone) {
      setForm((prev) => ({ ...prev, phone: member.phone || '' }));
    } else if (isWalkInMember(member) || gymId === WALK_IN_GYM_MEMBER_ID) {
      setForm((prev) => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // editableSelection can clear member on serial/name edit — re-resolve before failing
    let member = selected.member;
    const walkInHint = (
      isWalkInMember(member)
      || isWalkInGymMemberId(selected.gymId)
      || isWalkInMemberName(selected.name)
      || isWalkInGymMemberId(query)
      || isWalkInMemberName(query)
    );

    if (!member?.id && walkInHint) {
      try {
        member = await ensureWalkInMember();
        setSelected({
          name: WALK_IN_PAYMENT_NAME,
          gymId: WALK_IN_GYM_MEMBER_ID,
          member,
        });
      } catch (err) {
        toast.showError(err.message || 'Could not load walk-in customer.');
        return;
      }
    }

    if (!member?.id && (selected.gymId || selected.name || query.trim())) {
      const needle = String(selected.gymId || query || selected.name || '').trim();
      const hit = (members || []).find((m) => {
        if (!m?.id) return false;
        const serial = String(getGymMemberId(m) || '').trim();
        const name = String(m.name || '').trim().toLowerCase();
        return (
          (serial && serial === needle)
          || (name && name === needle.toLowerCase())
          || (selected.name && name === String(selected.name).trim().toLowerCase())
        );
      });
      if (hit) {
        member = hit;
        setSelected({
          name: hit.name || selected.name,
          gymId: getGymMemberId(hit) || selected.gymId,
          member: hit,
        });
      }
    }

    if (!member?.id) {
      toast.showError('Select a member or Walk-In Customer from the list (click a match).');
      return;
    }
    if (showBranchPicker && writeBranches.length > 0 && !writeBranch) {
      toast.showError('Select a branch for this sale.');
      return;
    }
    if (!form.phone.trim()) {
      toast.showError('Enter phone number.');
      return;
    }
    const amountBlank = String(form.amount ?? '').trim() === '';
    let payment = resolveSalePayment(amountBlank ? 0 : form.amount, form.paymentSplits);
    if (!Number.isFinite(payment.total) || payment.total < 0) {
      toast.showError('Enter a valid total amount, or leave blank to set later.');
      return;
    }
    // 0 paid is allowed (pay later) — sale stays pending until edited with a payment
    if (!Number.isFinite(payment.paid) || payment.paid < 0) {
      toast.showError('Enter a valid paid amount (0 is allowed for pay later).');
      return;
    }
    if (amountBlank && payment.paid > 0) {
      payment = { total: payment.paid, paid: payment.paid, left: 0, status: 'completed' };
    } else if (payment.paid > payment.total) {
      toast.showError('Total paid cannot exceed the sale total.');
      return;
    }
    if (!GUEST_PASS_DURATION_VALUES.includes(form.duration)) {
      toast.showError('Choose a valid duration.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const walkInSelected = isWalkInMember(member) || selected.gymId === WALK_IN_GYM_MEMBER_ID;
      const memberName = walkInSelected
        ? WALK_IN_PAYMENT_NAME
        : (member.name || selected.name);
      const gymId = walkInSelected
        ? WALK_IN_GYM_MEMBER_ID
        : (selected.gymId || getGymMemberId(member) || '');
      const guestPhone = form.phone.trim();
      const durationLabel = formatDurationLabel(form.duration, GUEST_PASS_DURATION_OPTIONS);
      const transactionDate = form.date || todayDate();
      const startDate = form.startDate || transactionDate;
      const endDate = form.endDate || guestPassEndDate({ startDate, duration: form.duration });
      const packageLabel = 'Guest Pass';
      const paymentSplits = serializePaymentSplits(form.paymentSplits);
      const paymentId = `PAY-${String(Date.now()).slice(-6)}`;
      const durationNote = `Duration: ${durationLabel} (${startDate} to ${endDate})`;
      const phoneNote = `Phone: ${guestPhone}`;
      const paymentNote = [form.note, durationNote, phoneNote].filter(Boolean).join(' · ');
      let invoiceId = '';

      // Payment row is the source of truth for history / finance / Z Report.
      const saved = await createPayment({
        paymentId,
        memberId: member.id,
        member: memberName,
        package: packageLabel,
        amount: payment.paid,
        packagePrice: payment.total,
        method: combineMethodLabels(form.paymentSplits),
        paymentSplits,
        date: transactionDate,
        status: payment.status,
        source: 'guest_pass',
        paymentNote,
        gymId,
        startDate,
        endDate,
        duration: durationLabel,
        branch: writeBranch,
      });
      invoiceId = saved?.invoiceId || '';

      // Mirror into guest_passes when possible (non-blocking — payment already saved).
      try {
        await createGuestPass({
          name: memberName,
          phone: guestPhone,
          duration: form.duration,
          startDate,
          endDate,
          fee: payment.paid,
          referredBy: memberName,
          referredByGymId: gymId,
          referredByMemberId: member.id,
        });
      } catch (guestErr) {
        console.warn('guest_passes mirror skipped:', guestErr?.message || guestErr);
      }

      const receiptData = buildSaleReceiptData({
        saleTitle: 'Guest Pass',
        saleLabel: 'Guest Pass',
        customer: memberName,
        gymId,
        phone: guestPhone,
        item: packageLabel,
        duration: form.duration,
        startDate,
        endDate,
        transactionDate,
        totalAmount: payment.total,
        paymentAmount: payment.paid,
        totalLeft: payment.left,
        paymentSplits: form.paymentSplits,
        paymentNote: form.note,
        status: payment.status,
        invoiceId,
        paymentId,
      });

      notifyPaymentsUpdated();
      toast.showSaved('Guest Pass');
      setReceipt(receiptData);
      setSaved(true);
      setLastSavedId(invoiceId);
      splitsTouchedRef.current = false;
      setForm(emptyForm());
      setQuery('');
      setSelected({ name: '', gymId: '', member: null });
      await loadData();
    } catch (err) {
      const message = err.message || 'Failed to add guest pass';
      setError(message);
      toast.showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleShowReceipt = (row) => {
    setReceipt(buildReceiptFromPaymentRow(row, { title: 'Guest Pass', label: 'Guest Pass' }));
  };

  const handleBalancePaid = async (receiptData) => {
    setReceipt(receiptData);
    await loadData();
  };

  const handleDeleteSale = async () => {
    if (!deleteTarget || deleting) return;
    const paymentDbId = deleteTarget.rawId || deleteTarget.id;
    try {
      setDeleting(true);
      await deletePayment(paymentDbId);
      setDeleteTarget(null);
      notifyPaymentsUpdated();
      toast.showDeleted('Guest Pass');
      await loadData();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete guest pass');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="rec-ancillary-sale">
      <PageHeader title="Guest Pass" subtitle="Issue guest passes for members or walk-in customers">
        {saved && lastSavedId && (
          <span className="owner-badge owner-badge-success">
            Guest pass saved — {lastSavedId}
          </span>
        )}
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading…</p>}
      {error && (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      )}

      <div className="owner-card rec-panel-card rec-ancillary-sale-form-card">
        <h2 className="owner-card-title">New Guest Pass</h2>
        <form className="subscription-form rec-ancillary-sale-form" onSubmit={handleSubmit}>
          <div className="rec-ancillary-sale-grid">
            <div className="owner-form-group rec-ancillary-sale-full">
              <GymIdMemberSearch
                id="guest-pass-member"
                label={MEMBER_SEARCH_LABEL}
                query={query}
                onQueryChange={setQuery}
                selectedName={selected.name}
                selectedGymId={selected.gymId}
                selectedMember={selected.member}
                onSelect={handleMemberSelect}
                members={members}
                editableSelection
                placeholder={MEMBER_SEARCH_PLACEHOLDER}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, alignItems: 'center' }}>
                <button
                  type="button"
                  className="owner-btn owner-btn-secondary owner-btn-sm"
                  onClick={async () => {
                    try {
                      const walkIn = await ensureWalkInMember();
                      setQuery(WALK_IN_GYM_MEMBER_ID);
                      setSelected({
                        name: WALK_IN_PAYMENT_NAME,
                        gymId: WALK_IN_GYM_MEMBER_ID,
                        member: walkIn,
                      });
                      setForm((prev) => ({ ...prev, phone: '' }));
                    } catch (err) {
                      toast.showError(err.message || 'Could not load walk-in customer.');
                    }
                  }}
                >
                  Walk-In Customer
                </button>
                {selected.gymId === WALK_IN_GYM_MEMBER_ID && (
                  <span className="owner-placeholder-text" style={{ margin: 0, fontSize: 12 }}>
                    Walk-in selected (Serial {WALK_IN_GYM_MEMBER_ID})
                  </span>
                )}
              </div>
              {members.length === 0 && query.trim() && !loading && (
                <p className="owner-placeholder-text" style={{ marginTop: 8, fontSize: 12 }}>
                  No members match this search. Use Walk-In Customer, or add members from the Members page.
                </p>
              )}
              {showBranchPicker && (
                <SaleBranchField
                  id="guest-pass-branch"
                  branches={writeBranches}
                  value={pickedBranch}
                  onChange={setPickedBranch}
                />
              )}
            </div>

            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="guest-pass-phone">Phone</label>
              <input
                id="guest-pass-phone"
                name="phone"
                className="owner-form-input"
                value={form.phone}
                onChange={handleChange}
                required
              />
            </div>

            <SaleDurationField
              idPrefix="guest-pass"
              duration={form.duration}
              startDate={form.startDate}
              endDate={form.endDate}
              onPeriodChange={handlePeriodChange}
              durationOptions={GUEST_PASS_DURATION_OPTIONS}
            />

            <SaleAmountFields
              idPrefix="guest-pass"
              totalAmount={form.amount}
              paymentSplits={form.paymentSplits}
              onTotalChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              onPaymentSplitsChange={(paymentSplits) => setForm((prev) => ({ ...prev, paymentSplits }))}
              onSplitsTouched={() => { splitsTouchedRef.current = true; }}
            />

            <div className="owner-form-group">
              <label className="owner-form-label" htmlFor="guest-pass-date">Date</label>
              <input
                id="guest-pass-date"
                name="date"
                type="date"
                className="owner-form-input"
                value={form.date}
                onChange={handleChange}
                required
              />
              <p className="owner-placeholder-text rec-sale-payment-hint">
                Payment / transaction date.
              </p>
            </div>

            <div className="owner-form-group rec-ancillary-sale-full">
              <label className="owner-form-label" htmlFor="guest-pass-note">Note</label>
              <input
                id="guest-pass-note"
                name="note"
                className="owner-form-input"
                value={form.note}
                onChange={handleChange}
                placeholder="Optional note"
              />
            </div>
          </div>

          <div className="subscription-form-actions">
            <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Guest Pass'}
            </button>
          </div>
        </form>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="rec-ancillary-sale-history-header">
          <div>
            <h2 className="owner-card-title">Guest Pass history</h2>
            <p className="owner-placeholder-text" style={{ margin: '6px 0 0' }}>
              {total} sales · {formatCurrency(totalCollected)}
              {(listRangeStart || listRangeEnd) ? ` · ${listRangeStart || '…'} → ${listRangeEnd || '…'}` : ''}
            </p>
          </div>
        </div>
        <PrintActionsHeader
          className="print-actions-header--card"
          startDate={listRangeStart}
          endDate={listRangeEnd}
          onStartDateChange={setRangeStart}
          onEndDateChange={setRangeEnd}
          onClearDates={receptionistScope ? undefined : () => { setRangeStart(''); setRangeEnd(''); }}
          datesReadOnly={receptionistScope}
          onPrint={() => handleExport(printGuestPassList)}
          onCsv={() => handleExport(exportGuestPassCsv)}
          onPdf={() => handleExport(exportGuestPassPdf)}
          onExcel={() => handleExport(exportGuestPassExcel)}
          onExport={() => handleExport(exportGuestPasses)}
          ariaLabel="Guest pass print and export"
        />
        {receptionistScope ? (
          <p className="owner-placeholder-text owner-mb-16">
            Pakistan time · today only — guest passes sold today
          </p>
        ) : null}
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="Guest Pass"
                  filterKey="item"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search pass..."
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search duration..."
                />
                <SaleMethodColumnHeaders
                  sales={sales}
                  filters={filters}
                  openFilterKey={openFilterKey}
                  toggleFilter={toggleFilter}
                  updateFilter={updateFilter}
                />
                <ColumnFilterHeader
                  label="Total"
                  filterKey="total"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search total..."
                />
                <ColumnFilterHeader
                  label="Total paid"
                  filterKey="totalPaid"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search paid..."
                />
                <ColumnFilterHeader
                  label="Total left"
                  filterKey="totalLeft"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search left..."
                />
                <ColumnFilterHeader
                  label="Start date"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="End date"
                  filterKey="endDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Note"
                  filterKey="note"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search note..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.length ? filteredSales.map((row) => (
                <tr key={row.rawId || row.id}>
                  <td>{saleDate(row)}</td>
                  <td className="rec-member-gym-id">{row.gymId || '—'}</td>
                  <td>{row.member || '—'}</td>
                  <td>{saleRowPackageName(row)}</td>
                  <td>{saleRowDuration(row)}</td>
                  <SaleMethodCells row={row} />
                  <td>{formatCurrency(saleRowTotal(row))}</td>
                  <td className="finance-amount-positive">{formatCurrency(saleRowPaid(row))}</td>
                  <td className={saleRowLeft(row) > 0 ? 'finance-amount-negative' : ''}>
                    {formatCurrency(saleRowLeft(row))}
                  </td>
                  <td>{saleRowStartDate(row)}</td>
                  <td>{saleRowEndDate(row)}</td>
                  <td>{row.paymentNote || '—'}</td>
                  <td>
                    <SaleHistoryRowActions
                      row={row}
                      onReceipt={handleShowReceipt}
                      onPayBalance={setPayTarget}
                      onEdit={setEditTarget}
                      onDelete={setDeleteTarget}
                    />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={HISTORY_COL_COUNT} className="owner-placeholder-text">
                    No guest passes yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>

      <RemainingClearHistory title="Guest Pass — Remaining Clear" />

      <SaleReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
      {editTarget && (
        <SaleEditModal
          key={editTarget.rawId || editTarget.id}
          row={editTarget}
          title="Edit Guest Pass"
          itemLabel="Guest Pass"
          itemPlaceholder="Guest Pass"
          durationOptions={GUEST_PASS_DURATION_OPTIONS}
          onClose={() => setEditTarget(null)}
          onSaved={loadData}
        />
      )}
      {payTarget && (
        <PayBalanceModal
          key={payTarget.rawId || payTarget.id}
          paymentRow={payTarget}
          saleMeta={{ title: 'Guest Pass', label: 'Guest Pass' }}
          onClose={() => setPayTarget(null)}
          onPaid={handleBalancePaid}
        />
      )}
      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Guest Pass</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete guest pass for <strong>{deleteTarget.member || 'this customer'}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDeleteSale} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Visitors;
